import { FormEvent, useEffect, useId, useState } from "react";

import { api } from "../../api/client";
import { AlertBanner } from "../../components/feedback/AlertBanner";
import { AnimalDetailModal } from '../../components/modals/AnimalDetailModal';
import { EarTag, useFormatEarTag } from "../../components/tags/EarTag";
import { AnimalsSubnav } from '../../components/subnav/AnimalsSubnav';
import { Field } from '../../components/forms/Field';
import { FormModal } from '../../components/modals/FormModal';
import { SortHeader } from '../../components/forms/SortHeader';
import { ResponsiveTable } from '../../components/ui/ResponsiveTable';
import { apiError } from "../../lib/apiError";
import {
  formatDate,
  PROGRAM_STATUS_LABELS,
  SEX_LABELS,
  ANIMAL_STATUS_LABELS,
} from "../../lib/format";
import { passwordFieldProps, useFilterFieldProps } from "../../lib/inputPrivacy";
import { DEFAULT_SPECIES } from "../../lib/species/species";
import { useTableControls } from "../../lib/tableControls";
import { loadWithReplica, mutateOrQueue, offlineQueuedInfo } from "../../lib/offline/helpers";
import {
  requireOfflineAdminPassword,
  storeAdminPasswordVerifier,
} from "../../lib/offline/offlineAdminPassword";
import { patchAnimalsReplica } from "../../lib/offline/replica";
import { animalsListFromReplica } from "../../lib/offline/sanitize";
import type { ProgramStatus, Sex, Animal } from "../../types";

type ArchiveReason = "dead" | "sold" | "slaughtered";

const ARCHIVE_REASONS: ArchiveReason[] = ["dead", "sold", "slaughtered"];

const EMPTY_FORM = {
  password: "",
  tag: "",
  sex: "female" as Sex,
  birth_date: "",
  arrival_date: "",
  mother_tag: "",
  breed: "",
  program_status: "unspecified" as ProgramStatus,
};

function formFromAnimal(animal: Animal) {
  return {
    password: "",
    tag: animal.tag,
    sex: animal.sex,
    birth_date: animal.birth_date ?? "",
    arrival_date: animal.arrival_date ?? "",
    mother_tag: animal.mother_tag ?? "",
    breed: animal.breed ?? "",
    program_status: animal.program_status,
  };
}

function animalValue(animal: Animal, key: string): unknown {
  switch (key) {
    case "tag":
      return animal.tag;
    case "sex":
      return animal.sex;
    case "birth_date":
      return animal.birth_date;
    case "breed":
      return animal.breed || "";
    case "program_status":
      return animal.program_status;
    default:
      return null;
  }
}

export function AnimalsPage() {
  const formatTag = useFormatEarTag();
  const [animals, setAnimals] = useState<Animal[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [archiveTag, setArchiveTag] = useState<string | null>(null);
  const [archiveReason, setArchiveReason] = useState<ArchiveReason>("dead");
  const [archivePassword, setArchivePassword] = useState("");
  const [deleteTag, setDeleteTag] = useState<string | null>(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [detailTag, setDetailTag] = useState<string | null>(null);
  const fieldNs = useId().replace(/:/g, "");
  const tagFilterProps = useFilterFieldProps("tag");

  const { view, sortKey, sortDir, filters, toggleSort, setFilter } = useTableControls({
    rows: animals,
    getValue: animalValue,
    defaultSortKey: "tag",
    defaultSortDir: "asc",
  });

  async function load() {
    const result = await loadWithReplica<Animal[]>(
      "animals:active",
      () => api.GET("/api/animals", { params: { query: { scope: "active" } } })
    );
    if (result.error && !result.fromReplica) {
      setError(apiError(result.error, "Nie udało się wczytać zwierząt."));
      return;
    }
    setError(null);
    setAnimals(animalsListFromReplica(result.data) as Animal[]);
  }

  useEffect(() => {
    void load();
  }, []);

  function closeForm() {
    setShowForm(false);
    setEditingTag(null);
    setForm(EMPTY_FORM);
  }

  function openCreateForm() {
    setArchiveTag(null);
    setEditingTag(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
    setError(null);
    setInfo(null);
  }

  function openEditForm(animal: Animal) {
    setArchiveTag(null);
    setEditingTag(animal.tag);
    setForm(formFromAnimal(animal));
    setShowForm(true);
    setError(null);
    setInfo(null);
  }

  function openArchiveForm(animal: Animal) {
    closeForm();
    setArchiveTag(animal.tag);
    setArchiveReason("dead");
    setArchivePassword("");
    setError(null);
    setInfo(null);
  }

  function closeArchiveForm() {
    setArchiveTag(null);
    setArchiveReason("dead");
    setArchivePassword("");
  }

  function openDeleteForm(animal: Animal) {
    closeForm();
    closeArchiveForm();
    setDeleteTag(animal.tag);
    setDeletePassword("");
    setError(null);
    setInfo(null);
  }

  function closeDeleteForm() {
    setDeleteTag(null);
    setDeletePassword("");
  }

  async function onSubmit(event: FormEvent, confirmClearOffspringTag = false) {
    event.preventDefault();
    if (!form.tag.trim() || !form.password) {
      setError("Podaj numer kolczyka i hasło administratora.");
      return;
    }
    
    const password = form.password;
    const verifyError = await requireOfflineAdminPassword(password);
    if (verifyError) {
      setError(verifyError);
      return;
    }
    
    const body = {
      password,
      tag: form.tag.trim(),
      species: DEFAULT_SPECIES,
      sex: form.sex,
      birth_date: form.birth_date || null,
      arrival_date: form.arrival_date || null,
      mother_tag: form.mother_tag.trim() || null,
      breed: form.breed.trim() || null,
      program_status: form.program_status,
      confirm_clear_offspring_tag: confirmClearOffspringTag,
      operation_id: editingTag ? crypto.randomUUID() : undefined,
    };
    
    setBusy(true);
    const result = editingTag
      ? await mutateOrQueue(
          "PUT",
          `/api/animals/${encodeURIComponent(editingTag)}`,
          body
        )
      : await mutateOrQueue(
          "POST",
          "/api/animals",
          body
        );
    setBusy(false);
    if (!result.success) {
      const message = apiError(
        result.error,
        editingTag ? "Nie udało się zapisać zwierzęcia." : "Nie udało się dodać zwierzęcia.",
      );
      if (
        !editingTag &&
        !confirmClearOffspringTag &&
        (message.includes("confirm_clear_offspring_tag") ||
          message.includes("przydzielony jagnięciu"))
      ) {
        const ok = window.confirm(
          `${message}\n\nPrzenieść numer do stada? Kolczyk zniknie z puli wolnych i nie wróci.`,
        );
        if (ok) {
          await onSubmit(event, true);
          return;
        }
      }
      setError(message);
      return;
    }
    
    if (result.online) {
      await storeAdminPasswordVerifier(password);
      if (result.data && typeof result.data === "object") {
        const saved = result.data as Animal;
        setAnimals((current) => {
          if (editingTag) {
            return current.map((animal) =>
              animal.tag === editingTag ? { ...animal, ...saved } : animal,
            );
          }
          return [
            ...current.filter((animal) => animal.tag !== saved.tag),
            saved,
          ];
        });
        await patchAnimalsReplica<Animal>("animals:active", (list) => {
          if (editingTag) {
            return list.map((animal) =>
              animal.tag === editingTag ? { ...animal, ...saved } : animal,
            );
          }
          return [
            ...list.filter((animal) => animal.tag !== saved.tag),
            saved,
          ];
        });
      }
    } else {
      const optimistic: Animal = {
        tag: body.tag,
        species: DEFAULT_SPECIES,
        sex: form.sex,
        birth_date: form.birth_date || null,
        arrival_date: form.arrival_date || null,
        mother_tag: form.mother_tag.trim() || null,
        breed: form.breed.trim() || null,
        program_status: form.program_status,
        status: "active",
      };
      if (!editingTag) {
        await patchAnimalsReplica<Animal>("animals:active", (list) => [
          ...list.filter((animal) => animal.tag !== optimistic.tag),
          optimistic,
        ]);
      } else {
        await patchAnimalsReplica<Animal>("animals:active", (list) =>
          list.map((animal) => (animal.tag === editingTag ? { ...animal, ...optimistic } : animal)),
        );
      }
      setAnimals((current) => {
        if (editingTag) {
          return current.map((animal) =>
            animal.tag === editingTag ? { ...animal, ...optimistic } : animal,
          );
        }
        return [
          ...current.filter((animal) => animal.tag !== optimistic.tag),
          optimistic,
        ];
      });
      setInfo(offlineQueuedInfo(result.needsPassword));
    }

    closeForm();
    await load();
  }

  async function onArchive(event: FormEvent) {
    event.preventDefault();
    if (!archiveTag) {
      return;
    }
    if (!archivePassword) {
      setError("Podaj hasło administratora.");
      return;
    }
    
    const password = archivePassword;
    const verifyError = await requireOfflineAdminPassword(password);
    if (verifyError) {
      setError(verifyError);
      return;
    }
    
    setBusy(true);
    const result = await mutateOrQueue(
      "POST",
      `/api/animals/${encodeURIComponent(archiveTag)}/archive`,
      {
        password,
        reason: archiveReason,
        operation_id: crypto.randomUUID(),
      }
    );
    setBusy(false);
    
    if (!result.success) {
      setError(apiError(result.error, "Nie udało się przenieść do archiwum."));
      return;
    }
    
    if (result.online) {
      await storeAdminPasswordVerifier(password);
    } else {
      let moved: Animal | undefined;
      await patchAnimalsReplica<Animal>("animals:active", (list) => {
        moved = list.find((animal) => animal.tag === archiveTag);
        return list.filter((animal) => animal.tag !== archiveTag);
      });
      if (moved) {
        const archived: Animal = {
          ...moved,
          status: archiveReason,
          archived_at: new Date().toISOString(),
        };
        await patchAnimalsReplica<Animal>("animals:archived", (list) => [
          ...list.filter((animal) => animal.tag !== archiveTag),
          archived,
        ]);
      }
      setInfo(offlineQueuedInfo(result.needsPassword));
    }

    closeArchiveForm();
    await load();
  }

  async function onDelete(event: FormEvent) {
    event.preventDefault();
    if (!deleteTag || !deletePassword) {
      setError("Podaj hasło administratora.");
      return;
    }
    if (!navigator.onLine) {
      setError("Trwałe usuwanie wymaga połączenia z siecią.");
      return;
    }
    setBusy(true);
    const deleted = await api.DELETE("/api/animals/{tag}", {
      params: { path: { tag: deleteTag } },
      body: { password: deletePassword },
    });
    setBusy(false);
    if (deleted.error) {
      setError(apiError(deleted.error, "Nie udało się usunąć zwierzęcia."));
      return;
    }
    closeDeleteForm();
    setInfo(`Usunięto zwierzę ${formatTag(deleteTag)}.`);
    await load();
  }

  return (
    <main className="page" data-testid="animals-page">
      <header className="page__header page__header--row">
        <div>
          <p className="page__kicker">Stado</p>
          <h1>Zwierzęta</h1>
          <p className="muted">{animals.length} aktywnych</p>
        </div>
        <button
          type="button"
          className="btn"
          data-testid="animals-add"
          onClick={openCreateForm}
        >
          Dodaj zwierzę
        </button>
      </header>
      <AnimalsSubnav />

      <AlertBanner variant="error" message={error} onDismiss={() => setError(null)} />
      <AlertBanner variant="info" message={info} onDismiss={() => setInfo(null)} />

      <FormModal
        open={showForm}
        onClose={closeForm}
        title={editingTag ? "Edytuj zwierzę" : "Nowe zwierzę"}
        size="lg"
        testId="animal-form"
        onSubmit={onSubmit}
      >
          <Field label="Numer kolczyka">
            <input
              className="input"
              data-testid="animal-tag"
              value={form.tag}
              onChange={(event) => setForm({ ...form, tag: event.target.value })}
              required
              autoComplete="off"
            />
          </Field>
          <div className="form__row">
            <Field label="Płeć">
              <select
                className="input"
                data-testid="animal-sex"
                value={form.sex}
                onChange={(event) => setForm({ ...form, sex: event.target.value as Sex })}
              >
                <option value="female">Samica</option>
                <option value="male">Samiec</option>
              </select>
            </Field>
            <Field label="Data urodzenia">
              <input
                className="input"
                type="date"
                value={form.birth_date}
                onChange={(event) => setForm({ ...form, birth_date: event.target.value })}
              />
            </Field>
          </div>
          <div className="form__row">
            <Field label="Data przybycia">
              <input
                className="input"
                type="date"
                value={form.arrival_date}
                onChange={(event) => setForm({ ...form, arrival_date: event.target.value })}
              />
            </Field>
            <Field label="Numer matki">
              <input
                className="input"
                value={form.mother_tag}
                onChange={(event) => setForm({ ...form, mother_tag: event.target.value })}
                autoComplete="off"
              />
            </Field>
          </div>
          <Field label="Rasa">
            <input
              className="input"
              data-testid="animal-breed"
              value={form.breed}
              onChange={(event) => setForm({ ...form, breed: event.target.value })}
            />
          </Field>
          <Field label="Program">
            <select
              className="input"
              value={form.program_status}
              onChange={(event) =>
                setForm({ ...form, program_status: event.target.value as ProgramStatus })
              }
            >
              {Object.entries(PROGRAM_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Hasło administratora">
            <input
              className="input"
              type="password"
              data-testid="animal-password"
              value={form.password}
              onChange={(event) => setForm({ ...form, password: event.target.value })}
              required
              {...passwordFieldProps(`${fieldNs}-animal-password`)}
            />
          </Field>
          <div className="modal__actions">
            <button type="button" className="btn btn--ghost" onClick={closeForm}>
              Anuluj
            </button>
            <button className="btn" type="submit" disabled={busy} data-testid="animal-save">
              Zapisz
            </button>
          </div>
      </FormModal>

      <FormModal
        open={Boolean(archiveTag)}
        onClose={closeArchiveForm}
        title="Przenieś do archiwum"
        testId="animal-archive-form"
        onSubmit={onArchive}
      >
          <p className="muted">
            Zgłoś zdarzenie dla kolczyka <strong><EarTag value={archiveTag} /></strong> — sztuka trafi do archiwum.
          </p>
          <Field label="Powód">
            <select
              className="input"
              data-testid="animal-archive-reason"
              value={archiveReason}
              onChange={(event) => setArchiveReason(event.target.value as ArchiveReason)}
            >
              {ARCHIVE_REASONS.map((reason) => (
                <option key={reason} value={reason}>
                  {ANIMAL_STATUS_LABELS[reason] || reason}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Hasło administratora">
            <input
              className="input"
              type="password"
              data-testid="animal-archive-password"
              value={archivePassword}
              onChange={(event) => setArchivePassword(event.target.value)}
              required
              {...passwordFieldProps(`${fieldNs}-animal-archive-password`)}
            />
          </Field>
          <div className="modal__actions">
            <button type="button" className="btn btn--ghost" onClick={closeArchiveForm}>
              Anuluj
            </button>
            <button
              className="btn btn--danger"
              type="submit"
              disabled={busy}
              data-testid="animal-archive-confirm"
            >
              Potwierdź
            </button>
          </div>
      </FormModal>

      <FormModal
        open={Boolean(deleteTag)}
        onClose={closeDeleteForm}
        title="Usuń trwale"
        testId="animal-delete-form"
        onSubmit={onDelete}
      >
        <p className="muted">
          Trwale usuń zwierzę <strong><EarTag value={deleteTag} /></strong>. Tej operacji nie można cofnąć.
        </p>
        <Field label="Hasło administratora">
          <input
            className="input"
            type="password"
            data-testid="animal-delete-password"
            value={deletePassword}
            onChange={(event) => setDeletePassword(event.target.value)}
            required
            {...passwordFieldProps(`${fieldNs}-animal-delete-password`)}
          />
        </Field>
        <div className="modal__actions">
          <button type="button" className="btn btn--ghost" onClick={closeDeleteForm}>
            Anuluj
          </button>
          <button
            className="btn btn--danger"
            type="submit"
            disabled={busy}
            data-testid="animal-delete-confirm"
          >
            Usuń trwale
          </button>
        </div>
      </FormModal>

      <section className="panel">
        <div className="filters">
          <input
            className="input"
            type="search"
            placeholder="Szukaj numeru kolczyka…"
            value={filters.tag ?? ""}
            onChange={(event) => setFilter("tag", event.target.value)}
            {...tagFilterProps}
          />
          <select
            className="input"
            value={filters.sex ?? ""}
            onChange={(event) => setFilter("sex", event.target.value)}
            aria-label="Filtruj płeć"
          >
            <option value="">Płeć: wszystkie</option>
            <option value="female">Samica</option>
            <option value="male">Samiec</option>
          </select>
          <select
            className="input"
            value={filters.program_status ?? ""}
            onChange={(event) => setFilter("program_status", event.target.value)}
            aria-label="Filtruj program"
          >
            <option value="">Program: wszystkie</option>
            {Object.entries(PROGRAM_STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div className="table-wrap">
          <ResponsiveTable className="tbl" data-testid="animals-table">
            <thead>
              <tr>
                <th>
                  <SortHeader
                    label="Kolczyk"
                    sortKey="tag"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Płeć"
                    sortKey="sex"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Urodzenie"
                    sortKey="birth_date"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Rasa"
                    sortKey="breed"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>
                  <SortHeader
                    label="Program"
                    sortKey="program_status"
                    activeKey={sortKey}
                    sortDir={sortDir}
                    onToggle={toggleSort}
                  />
                </th>
                <th>Akcje</th>
              </tr>
            </thead>
            <tbody>
              {view.length === 0 ? (
                <tr className="tbl__empty-row">
                  <td colSpan={6} className="muted">
                    Brak zwierząt spełniających filtr.
                  </td>
                </tr>
              ) : (
                view.map((animal) => (
                  <tr key={animal.tag}>
                    <td>
                      <button
                        type="button"
                        className="irz-tag-link"
                        data-testid="animal-tag-link"
                        onClick={() => setDetailTag(animal.tag)}
                      >
                        <EarTag value={animal.tag} />
                      </button>
                    </td>
                    <td>{SEX_LABELS[animal.sex] || animal.sex}</td>
                    <td>{formatDate(animal.birth_date)}</td>
                    <td>{animal.breed || "—"}</td>
                    <td>{PROGRAM_STATUS_LABELS[animal.program_status] || animal.program_status}</td>
                    <td>
                      <div className="actions">
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          data-testid="animal-edit"
                          onClick={() => openEditForm(animal)}
                        >
                          Edytuj
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          data-testid="animal-archive"
                          onClick={() => openArchiveForm(animal)}
                        >
                          Zdarzenie
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm btn--danger"
                          data-testid="animal-delete"
                          onClick={() => openDeleteForm(animal)}
                        >
                          Usuń
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </ResponsiveTable>
        </div>
      </section>
      <AnimalDetailModal
        tag={detailTag}
        animals={animals}
        onClose={() => setDetailTag(null)}
        onOpenTag={setDetailTag}
      />
    </main>
  );
}
